/**
 * Feuchteberechnungen nach der Magnus-Formel.
 * Koeffizienten nach Sonntag (1990), WMO-Empfehlung, gueltig -45 .. +60 Grad C ueber Wasser.
 * Herleitung und Quellen: docs/02-berechnungsmodell.md
 */

/** Magnus-Koeffizienten ueber Wasser. */
const MAGNUS_A = 17.62
const MAGNUS_B = 243.12
/** Saettigungsdampfdruck bei 0 Grad C in hPa. */
const E0 = 6.112

/** 10^5 * m_w / R* mit m_w = 18.016 kg/kmol und R* = 8314.3 J/(kmol*K). */
const AH_FACTOR = 216.679

/** Normaldruck auf Meereshoehe in hPa. */
const P_STANDARD = 1013.25

const KELVIN = 273.15

/** Saettigungsdampfdruck der Luft in hPa bei Temperatur `t` in Grad C. */
export function saturationVapourPressure(t: number): number {
  return E0 * Math.exp((MAGNUS_A * t) / (MAGNUS_B + t))
}

/** Tatsaechlicher Wasserdampf-Partialdruck in hPa. */
export function vapourPressure(t: number, rh: number): number {
  return (rh / 100) * saturationVapourPressure(t)
}

/**
 * Taupunkttemperatur in Grad C.
 * Bei rh <= 0 ist der Taupunkt physikalisch nicht definiert; wir liefern -Infinity,
 * damit Vergleiche wie `dewPoint > surface` zuverlaessig falsch werden.
 */
export function dewPoint(t: number, rh: number): number {
  if (rh <= 0) return Number.NEGATIVE_INFINITY
  const v = Math.log(vapourPressure(t, rh) / E0)
  return (MAGNUS_B * v) / (MAGNUS_A - v)
}

/** Absolute Luftfeuchte in g/m^3. */
export function absoluteHumidity(t: number, rh: number): number {
  return (AH_FACTOR * vapourPressure(t, rh)) / (t + KELVIN)
}

/** Mischungsverhaeltnis in g Wasser je kg trockener Luft. */
export function mixingRatio(t: number, rh: number, pressure = P_STANDARD): number {
  const e = vapourPressure(t, rh)
  return (622 * e) / (pressure - e)
}

/**
 * Relative Feuchte, die Luft mit dem Feuchtegehalt von (`t`, `rh`) annimmt,
 * wenn sie auf `targetT` erwaermt oder abgekuehlt wird. Auf 0..100 begrenzt.
 */
export function relativeHumidityAt(t: number, rh: number, targetT: number): number {
  const ratio = (100 * vapourPressure(t, rh)) / saturationVapourPressure(targetT)
  return Math.min(100, Math.max(0, ratio))
}

export interface Reading {
  /** Temperatur in Grad C. */
  t: number
  /** Relative Feuchte in Prozent. */
  rh: number
}

export interface DerivedReading extends Reading {
  /** Absolute Feuchte in g/m^3. */
  absolute: number
  /** Taupunkt in Grad C. */
  dewPoint: number
  /** Mischungsverhaeltnis in g/kg. */
  mixingRatio: number
}

export function derive(reading: Reading): DerivedReading {
  const { t, rh } = reading
  return {
    t,
    rh,
    absolute: absoluteHumidity(t, rh),
    dewPoint: dewPoint(t, rh),
    mixingRatio: mixingRatio(t, rh),
  }
}
